import { Outlet, Navigate } from 'react-router-dom';
import { usePermissions } from 'hooks/usePermissions';
import type { AppPermission } from 'utils/permissions';

interface RouteGuardProps {
  /** Require this single permission */
  permission?: AppPermission;
  /** Require ANY of these permissions */
  permissions?: AppPermission[];
  /** Require this role (or any of these roles) */
  role?: string | string[];
  /** Where to redirect when access is denied. Defaults to /terceros */
  redirectTo?: string;
}

/**
 * Route-level access guard. Renders <Outlet /> when authorized, redirects otherwise.
 * Use as the `element` on a parent route to protect all its children.
 *
 * Example in MainRoutes.tsx:
 *   { path: 'admin', element: <RouteGuard role="Administrador" />, children: [...] }
 */
export function RouteGuard({ permission, permissions, role, redirectTo = '/terceros' }: RouteGuardProps) {
  const { can, canAny, hasRole } = usePermissions();

  let allowed = true;

  if (role !== undefined) {
    allowed = hasRole(role);
  }

  if (allowed && permission !== undefined) {
    allowed = can(permission);
  }

  if (allowed && permissions !== undefined && permissions.length > 0) {
    allowed = canAny(permissions);
  }

  return allowed ? <Outlet /> : <Navigate to={redirectTo} replace />;
}
